import React, { useState, useEffect } from 'react';
import {
  Minus,
  Plus,
  Utensils,
  ShoppingCart,
  X,
  Check,
  MessageCircle
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import { toast } from 'react-toastify';
import { db } from '../firebase'; // Assume you have a firebase.js to initialize Firebase
import { collection, query, doc, onSnapshot } from 'firebase/firestore';

// Available swallow options and additional price for extra swallows.
const availableSwallows = [
  { name: 'Poundo', isAvailable: true },
  { name: 'Semo', isAvailable: true },
  { name: 'Eba', isAvailable: true }
];
const additionalSwallowPrice = 1500;

const SoupComponent = () => {
  const dispatch = useDispatch();

  // State to hold menu items fetched from Firestore.
  const [items, setItems] = useState([]);

  // State to hold selections for each soup order.
  const [selections, setSelections] = useState({});

  // Modal state for additional swallows.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // State for chef note modal and its comment.
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [chefComment, setChefComment] = useState('');

  // Set up a real-time Firestore listener on the "Afrikana" document.
  useEffect(() => {
    const docRef = doc(db, 'menuCategories', 'Afrikana');
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          // We expect the document to have an "items" field holding an array of menu items.
          setItems(docSnapshot.data().items || []);
        } else {
          setItems([]);
        }
      },
      (error) => {
        console.error("Error fetching Afrikana data:", error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Update the free swallow selection for a given item.
  const handleFreeSwallowChange = (itemId, value) => {
    setSelections((prev) => {
      const additional = (prev[itemId]?.extras?.swallows || []).filter(sw => sw.price !== 0);
      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          extras: {
            swallows: [{ name: value, price: 0, quantity: 1 }].concat(additional)
          }
        }
      };
    });
  };

  // Toggle an additional swallow selection.
  const handleAdditionalSwallowChange = (itemId, swallowName, isChecked) => {
    setSelections((prev) => {
      const currentSwallows = prev[itemId]?.extras?.swallows || [];
      if (isChecked) {
        return {
          ...prev,
          [itemId]: {
            ...prev[itemId],
            extras: {
              swallows: [
                ...currentSwallows,
                { name: "Extra " + swallowName, price: additionalSwallowPrice, quantity: 1 }
              ]
            }
          }
        };
      } else {
        return {
          ...prev,
          [itemId]: {
            ...prev[itemId],
            extras: {
              swallows: currentSwallows.filter(
                (sw) => !(sw.name === "Extra " + swallowName && sw.price === additionalSwallowPrice)
              )
            }
          }
        };
      }
    });
  };

  // Adjust the quantity for an extra swallow.
  const handleExtraSwallowQuantityChange = (itemId, swallowName, delta) => {
    setSelections((prev) => {
      const updatedSwallows = (prev[itemId]?.extras?.swallows || []).map((swallow) => {
        if (swallow.name === "Extra " + swallowName && swallow.price === additionalSwallowPrice) {
          const newQuantity = Math.max(1, swallow.quantity + delta);
          return { ...swallow, quantity: newQuantity };
        }
        return swallow;
      });
      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          extras: {
            swallows: updatedSwallows
          }
        }
      };
    });
  };

  // Adjust the overall soup quantity.
  const handleQuantityChange = (itemId, delta) => {
    setSelections((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.max(1, (prev[itemId]?.quantity || 1) + delta)
      }
    }));
  };

  // Opens the modal for selecting additional swallows.
  const handleOpenModal = (itemId) => {
    // Find the item from the Firestore items.
    const item = items.find(item => item.id === itemId);
    setSelectedItem(item);
    setSelectedItemId(itemId);
    setIsModalOpen(true);
  };

  // Closes the additional swallows modal.
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItemId(null);
    setSelectedItem(null);
  };

  // Adds the soup order to the cart.
  const handleAddToCart = (item) => {
    const itemSelections = selections[item.id] || {};
    if (!itemSelections.extras?.swallows?.some(sw => sw.price === 0 && sw.name)) {
      toast.error("Please select your free swallow", {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      name: item.name,
      price: item.price,
      quantity: itemSelections.quantity || 1,
      extras: itemSelections.extras || {},
      chefComment: chefComment
    };
    dispatch(addToCart(cartItem));
    toast.success("Soup added to cart!", {
      position: "top-right",
      autoClose: 2000
    });
  };

  // Renders each soup item card.
  const renderMenuItem = (item) => {
    const itemSelections = selections[item.id] || {};
    const quantity = itemSelections.quantity || 1;

    return (
      <div key={item.id} className="bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800 relative">
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          {/* Overlay for unavailable items */}
          {!item.isAvailable && (
            <div className="absolute inset-0 bg-black bg-opacity-75 flex justify-center items-center">
              <span className="text-red-600 font-bold text-xl">Not Available</span>
            </div>
          )}
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-600 text-white font-medium text-lg py-1 px-3 rounded-full">
                ₦{item.price.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-red-500 font-bold text-xl">{item.name}</h3>
            <p className="text-gray-400 mt-1 text-sm">{item.description}</p>
          </div>

          {item.isAvailable && (
            <>
              {/* Free Swallow Selection (unchanged) */}
              <div className="relative">
                <select
                  className="w-full appearance-none bg-black border border-gray-700 text-gray-300 py-2 px-3 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  value={itemSelections.extras?.swallows?.[0]?.name || ''}
                  onChange={(e) => handleFreeSwallowChange(item.id, e.target.value)}
                >
                  <option value="">Select Free Swallow</option>
                  {availableSwallows.map((swallow, idx) => (
                    <option key={idx} value={swallow.name} disabled={!swallow.isAvailable}>
                      {swallow.name} { !swallow.isAvailable && '(Not Available)' }
                    </option>
                  ))}
                </select>
                <Utensils className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
              </div>

              {/* Additional Swallows and Chef Note arranged side-by-side */}
              <div className="flex flex-row gap-2">
                <button
                  onClick={() => handleOpenModal(item.id)}
                  className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 flex items-center justify-center gap-2 transition-colors"
                >
                  <Utensils className="w-4 h-4" />
                  <span>Additional Swallows</span>
                </button>
                <button
                  onClick={() => setIsCommentModalOpen(true)}
                  className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Chef Note</span>
                </button>
              </div>

              {/* Quantity and Add to Cart arranged side-by-side */}
              <div className="flex flex-row gap-2 pt-2 items-center">
                <div className="flex items-center gap-2 bg-black rounded-lg p-1">
                  <button
                    onClick={() => handleQuantityChange(item.id, -1)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-medium text-red-500">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(item.id, 1)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => handleAddToCart(item)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>Add to Cart</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-red-500">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map(item => renderMenuItem(item))}
        </div>
      </div>

      {/* Additional Swallows Modal */}
      {isModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Additional Swallows</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {availableSwallows.map((swallow, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between gap-4 ${!swallow.isAvailable ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`${selectedItemId}-${swallow.name}-additional`}
                      checked={!!(selections[selectedItemId]?.extras?.swallows?.find(s => s.name === "Extra " + swallow.name))}
                      onChange={(e) => handleAdditionalSwallowChange(selectedItemId, swallow.name, e.target.checked)}
                      className="w-4 h-4 text-red-500 rounded border-gray-700 focus:ring-red-500 focus:ring-2"
                      disabled={!swallow.isAvailable}
                    />
                    <label htmlFor={`${selectedItemId}-${swallow.name}-additional`} className="text-gray-200">
                      {swallow.name} { !swallow.isAvailable && <span className="text-xs text-red-500">(Not Available)</span> }
                      <span className="text-sm text-gray-400 block">₦{additionalSwallowPrice.toLocaleString()}</span>
                    </label>
                  </div>
                  {selections[selectedItemId]?.extras?.swallows?.find(s => s.name === "Extra " + swallow.name) && (
                    <div className="flex items-center gap-2 bg-black rounded-lg p-1">
                      <button
                        onClick={() => handleExtraSwallowQuantityChange(selectedItemId, swallow.name, -1)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium text-red-500">
                        {selections[selectedItemId]?.extras?.swallows?.find(s => s.name === "Extra " + swallow.name)?.quantity || 1}
                      </span>
                      <button
                        onClick={() => handleExtraSwallowQuantityChange(selectedItemId, swallow.name, 1)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
              >
                <Check className="w-4 h-4" />
                <span>Done</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chef Note Modal */}
      {isCommentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Note for the Chef</h3>
              <button onClick={() => setIsCommentModalOpen(false)} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={chefComment}
              onChange={(e) => setChefComment(e.target.value)}
              placeholder="Any special requests or preferences?"
              className="w-full h-32 p-3 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="px-6 py-2 border border-gray-600 text-gray-200 hover:bg-gray-800 rounded-lg transition-colors focus:ring-2 focus:ring-red-500"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors focus:ring-2 focus:ring-red-500"
              >
                <Check className="w-4 h-4" />
                <span>Save Note</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoupComponent;
